import React, { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { fuzzyMatch } from "../lib/fuzzySearch";
import { HelpBubble } from "../components/HelpBubble";
import {
  Users,
  Shield,
  Edit,
  Trash2,
  Search,
  UserPlus,
  Ban,
  CheckCircle,
  Lock,
  Crown,
  Loader2,
  AlertTriangle,
  Mail,
  Send,
  Clock,
  RotateCcw,
  X,
  Copy,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const roleLabels = {
  superuser: "Superuser",
  admin: "System Administrator",
  legal: "Legal Team",
  contract_creator: "Contract Creator",
  contract_owner: "Contract Owner",
  finance: "Finance Team",
  executive: "Executive",
  external: "External User",
};

const roleColors = {
  superuser: "bg-red-100 text-red-700 border-red-200",
  admin: "bg-purple-100 text-purple-700",
  legal: "bg-blue-100 text-blue-700",
  contract_creator: "bg-cyan-100 text-cyan-700",
  contract_owner: "bg-slate-100 text-slate-700",
  finance: "bg-emerald-100 text-emerald-700",
  executive: "bg-amber-100 text-amber-700",
  external: "bg-gray-100 text-gray-700",
};

const pageLabels = {
  dashboard: "Dashboard",
  contracts: "Contracts",
  "contract-management": "Contract Management",
  templates: "Templates",
  clauses: "Clause Library",
  workflows: "Workflows",
  compliance: "Compliance",
  reports: "Reports",
  users: "User Management",
  "approval-policies": "Approval Policies",
  settings: "Settings",
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Add user dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", password: "", role: "contract_creator", department: "" });

  // Edit user dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "", department: "", is_active: true });

  // Permissions dialog
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [permList, setPermList] = useState([]);
  const [allPages, setAllPages] = useState([]);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Seat limit
  const [seatInfo, setSeatInfo] = useState(null);

  // Invitations
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", name: "", role: "contract_creator", department: "", additional_roles: [] });
  const [inviteSending, setInviteSending] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [showInvites, setShowInvites] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiClient.get("/users");
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) toast.error("Access denied");
      else toast.error("Failed to load users");
    } finally { setLoading(false); }
  }, []);

  const fetchSeatInfo = useCallback(async () => {
    try {
      const res = await apiClient.get("/organization/seats");
      setSeatInfo(res.data);
    } catch (e) { /* Seat info is optional UI enhancement */ }
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const res = await apiClient.get("/invitations");
      setInvitations(res.data);
    } catch (e) { /* Invitations load silently */ }
  }, []);

  useEffect(() => { fetchUsers(); fetchSeatInfo(); fetchInvitations(); }, [fetchUsers, fetchSeatInfo, fetchInvitations]);

  const isSuperuser = currentUser?.role === "superuser";
  const isAdminOrAbove = currentUser?.role === "superuser" || currentUser?.role === "admin" || currentUser?.role === "legal";


  const pendingInvites = invitations.filter(i => i.status === "pending");

  // --- Add User ---
  const handleAddUser = async () => {
    if (!addForm.name || !addForm.email || !addForm.password) {
      toast.error("Name, email, and password are required"); return;
    }
    try {
      await apiClient.post("/users", addForm);
      toast.success("User created successfully");
      setAddDialogOpen(false);
      setAddForm({ name: "", email: "", password: "", role: "contract_creator", department: "" });
      fetchUsers();
      fetchSeatInfo();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create user"); }
  };

  // --- Edit User ---
  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({ name: user.name, role: user.role, department: user.department || "", is_active: user.is_active });
    setEditDialogOpen(true);
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    try {
      await apiClient.put(`/users/${selectedUser.id}`, editForm);
      toast.success("User updated");
      setEditDialogOpen(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update user"); }
  };

  // --- Toggle Active ---
  const handleToggleActive = async (user) => {
    try {
      const res = await apiClient.put(`/users/${user.id}/toggle-active`);
      toast.success(res.data.message);
      fetchUsers();
      fetchSeatInfo();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // --- Delete User ---
  const handleDeleteUser = async () => {
    if (!deleteTarget) return;
    try {
      await apiClient.delete(`/users/${deleteTarget.id}`);
      toast.success("User deleted");
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchUsers();
      fetchSeatInfo();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete user"); }
  };

  // --- Invite User ---
  const handleSendInvite = async () => {
    if (!inviteForm.email) { toast.error("Email is required"); return; }
    setInviteSending(true);
    try {
      const res = await apiClient.post("/invitations", inviteForm);
      toast.success(`Invitation sent to ${inviteForm.email}`);
      // Copy invite link
      if (res.data.invite_link) {
        try { await navigator.clipboard.writeText(res.data.invite_link); toast.info("Invite link copied to clipboard"); } catch (e) { /* Clipboard API not available */ }
      }
      setInviteDialogOpen(false);
      setInviteForm({ email: "", name: "", role: "contract_creator", department: "", additional_roles: [] });
      fetchInvitations();
      fetchSeatInfo();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send invitation"); }
    finally { setInviteSending(false); }
  };

  // Revoke confirmation
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const handleRevokeInvite = async () => {
    if (!revokeTarget) return;
    try {
      await apiClient.delete(`/invitations/${revokeTarget.id}`);
      toast.success("Invitation revoked");
      setRevokeDialogOpen(false);
      setRevokeTarget(null);
      fetchInvitations();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to revoke"); }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await apiClient.post(`/invitations/${inviteId}/resend`);
      toast.success("Invitation resent");
      fetchInvitations();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resend"); }
  };

  // --- Permissions ---
  const openPermissions = async (user) => {
    setPermUser(user);
    setPermList(user.page_permissions || []);
    try {
      const res = await apiClient.get("/permissions/defaults");
      setAllPages(res.data.all_pages || []);
    } catch (e) { /* Page permissions load is best-effort */ }
    setPermDialogOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!permUser) return;
    try {
      await apiClient.put(`/users/${permUser.id}/permissions`, permList);
      toast.success("Permissions updated");
      setPermDialogOpen(false);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const togglePermission = (page) => {
    setPermList((prev) => prev.includes(page) ? prev.filter((p) => p !== page) : [...prev, page]);
  };

  const filteredUsers = users.filter((user) => {
    if (!searchQuery) return true;
    return fuzzyMatch(searchQuery, [user.name, user.email, user.role]);
  });

  if (!isAdminOrAbove) {
    return (
      <div className="text-center py-20">
        <Shield size={48} className="mx-auto text-slate-300 mb-4" />
        <p className="text-slate-500">Admin access required</p>
        <p className="text-sm text-slate-400 mt-1">You don&apos;t have permission to view this page</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
            User Management <HelpBubble tip="Add users directly or send email invitations. Assign roles, manage seat limits, control page-level permissions, and set per-user AI daily limits." />
          </h1>
          <p className="text-slate-500 mt-1">
            {isSuperuser ? "Full access control — add, edit, and manage user permissions" : "View and manage user roles"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Seat usage indicator */}
          {seatInfo && (
            <div className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 dark:border-slate-700 rounded-sm bg-white dark:bg-slate-900" data-testid="seat-usage-indicator">
              <Users size={14} className="text-slate-400" />
              <span className={`text-sm font-medium ${seatInfo.limit_reached ? "text-red-600" : "text-slate-700 dark:text-slate-300"}`}>
                {seatInfo.active_users}/{seatInfo.max_users} seats
              </span>
              {seatInfo.limit_reached && <AlertTriangle size={14} className="text-red-500" />}
              <HelpBubble tip={`Your plan allows ${seatInfo.max_users} active users. ${seatInfo.seats_remaining} seats remaining. Deactivate unused accounts or upgrade to add more.`} />
            </div>
          )}
          {(isSuperuser || currentUser?.role === "admin") && (
            <>
              <Button
                variant="outline"
                onClick={() => setInviteDialogOpen(true)}
                className="gap-2 rounded-sm"
                disabled={seatInfo?.limit_reached}
                data-testid="invite-user-btn"
                title={seatInfo?.limit_reached ? "Seat limit reached" : "Send an email invitation"}
              >
                <Mail size={16} /> Invite
              </Button>
              <Button
                onClick={() => setAddDialogOpen(true)}
                className="gap-2 rounded-sm"
                disabled={seatInfo?.limit_reached}
                data-testid="add-user-btn"
                title={seatInfo?.limit_reached ? "Seat limit reached — upgrade your plan or deactivate unused accounts" : "Add a new user"}
              >
                <UserPlus size={16} /> Add User
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Seat limit warning */}
      {seatInfo?.limit_reached && (
        <div className="flex items-center gap-3 p-3 border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 rounded-sm" data-testid="seat-limit-warning">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-800 dark:text-amber-400">
              User seat limit reached ({seatInfo.active_users}/{seatInfo.max_users})
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">
              Deactivate or remove unused accounts, or upgrade your subscription plan to add more users.
            </p>
          </div>
        </div>
      )}

      {/* Pending Invitations */}
      {(isSuperuser || currentUser?.role === "admin") && pendingInvites.length > 0 && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-sm" data-testid="pending-invites-section">
          <button
            onClick={() => setShowInvites(!showInvites)}
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            data-testid="toggle-pending-invites"
          >
            <div className="flex items-center gap-2">
              <Send size={14} className="text-sky-500" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Pending Invitations ({pendingInvites.length})
              </span>
            </div>
            <span className="text-xs text-slate-400">{showInvites ? "Hide" : "Show"}</span>
          </button>
          {showInvites && (
            <div className="border-t border-slate-100 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
              {pendingInvites.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`invite-row-${inv.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-sky-100 dark:bg-sky-900 flex items-center justify-center">
                      <Mail size={14} className="text-sky-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{inv.email}</p>
                      <p className="text-[11px] text-slate-400">
                        {roleLabels[inv.role] || inv.role} &middot; Invited by {inv.invited_by_name || inv.invited_by}
                        {inv.expires_at && (
                          <span className="ml-1">
                            &middot; Expires {new Date(inv.expires_at).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleResendInvite(inv.id)} title="Resend" data-testid={`resend-invite-${inv.id}`}>
                      <RotateCcw size={13} className="text-sky-500" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setRevokeTarget(inv); setRevokeDialogOpen(true); }} title="Revoke" data-testid={`revoke-invite-${inv.id}`}>
                      <X size={13} className="text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <Input placeholder="Search users..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-sm" data-testid="search-users-input" />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-slate-400" size={32} /></div>
        ) : filteredUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="contracts-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th className="w-36">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const isSelf = user.id === currentUser?.id;
                  const isTargetSuperuser = user.role === "superuser";
                  return (
                    <tr key={user.id} data-testid={`user-row-${user.id}`} className={!user.is_active ? "opacity-50" : ""}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium text-sm ${isTargetSuperuser ? "bg-red-600" : "bg-slate-900 dark:bg-cyan-500 dark:text-slate-900"}`}>
                            {isTargetSuperuser ? <Crown size={14} /> : user.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className="font-medium">{user.name}</span>
                            {isSelf && <span className="ml-2 text-xs text-slate-400">(You)</span>}
                          </div>
                        </div>
                      </td>
                      <td className="text-slate-600 dark:text-slate-400">{user.email}</td>
                      <td>
                        <Badge className={`rounded-sm ${roleColors[user.role] || "bg-slate-100 text-slate-700"}`}>
                          {roleLabels[user.role] || user.role}
                        </Badge>
                      </td>
                      <td className="text-slate-600 dark:text-slate-400">{user.department || "-"}</td>
                      <td>
                        <Badge className={`rounded-sm ${user.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                          {user.is_active ? "Active" : "Disabled"}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          {isAdminOrAbove && !isSelf && (
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.id}`} title="Edit user">
                              <Edit size={14} />
                            </Button>
                          )}
                          {isSuperuser && !isSelf && !isTargetSuperuser && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openPermissions(user)} data-testid={`perm-user-${user.id}`} title="Manage permissions">
                                <Lock size={14} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleToggleActive(user)} data-testid={`toggle-user-${user.id}`} title={user.is_active ? "Disable" : "Enable"}>
                                {user.is_active ? <Ban size={14} className="text-amber-500" /> : <CheckCircle size={14} className="text-emerald-500" />}
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => { setDeleteTarget(user); setDeleteDialogOpen(true); }} data-testid={`delete-user-${user.id}`} title="Delete user">
                                <Trash2 size={14} />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-20">
            <Users size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No users found</p>
          </div>
        )}
      </div>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new user account with role and department</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="John Smith" className="rounded-sm" data-testid="add-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="john@company.com" className="rounded-sm" data-testid="add-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input type="password" value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} placeholder="Min 8 characters" className="rounded-sm" data-testid="add-user-password" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Primary Role</Label>
                <Select value={addForm.role} onValueChange={(v) => setAddForm({ ...addForm, role: v, additional_roles: [] })}>
                  <SelectTrigger className="rounded-sm" data-testid="add-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleLabels).filter(([k]) => k !== "superuser").map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input value={addForm.department} onChange={(e) => setAddForm({ ...addForm, department: e.target.value })} placeholder="e.g. Legal" className="rounded-sm" data-testid="add-user-department" />
              </div>
            </div>
            {addForm.role === "contract_creator" && (
              <div className="space-y-2">
                <Label className="text-xs">Additional Roles (Dual Role)</Label>
                <div className="flex gap-3">
                  {[{v: "contract_owner", l: "Contract Owner"}, {v: "legal", l: "Legal"}].map(r => (
                    <label key={r.v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(addForm.additional_roles || []).includes(r.v)}
                        onChange={(e) => {
                          const current = addForm.additional_roles || [];
                          setAddForm({
                            ...addForm,
                            additional_roles: e.target.checked ? [...current, r.v] : current.filter(x => x !== r.v),
                          });
                        }}
                        className="rounded"
                        data-testid={`dual-role-${r.v}`}
                      />
                      {r.l}
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-slate-400">Contract Creators can also serve as Contract Owner and/or Legal</p>
              </div>
            )}
            <p className="text-xs text-slate-400 flex items-center gap-1.5 pt-1 border-t border-slate-100 dark:border-slate-800">
              <Mail size={12} /> Want to let the user set their own password? Use <button className="text-sky-600 underline" onClick={() => { setAddDialogOpen(false); setInviteDialogOpen(true); }}>Invite</button> instead.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleAddUser} className="rounded-sm" data-testid="confirm-add-user-btn">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail size={18} /> Invite User</DialogTitle>
            <DialogDescription>Send an email invitation — the user will set their own password</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="colleague@company.com"
                className="rounded-sm"
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Name <span className="text-slate-400 text-xs">(optional — they can set it themselves)</span></Label>
              <Input
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                placeholder="Jane Doe"
                className="rounded-sm"
                data-testid="invite-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v, additional_roles: [] })}>
                  <SelectTrigger className="rounded-sm" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(roleLabels).filter(([k]) => k !== "superuser").map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input
                  value={inviteForm.department}
                  onChange={(e) => setInviteForm({ ...inviteForm, department: e.target.value })}
                  placeholder="e.g. Legal"
                  className="rounded-sm"
                  data-testid="invite-department-input"
                />
              </div>
            </div>
            {inviteForm.role === "contract_creator" && (
              <div className="space-y-2">
                <Label className="text-xs">Additional Roles (Dual Role)</Label>
                <div className="flex gap-3">
                  {[{v: "contract_owner", l: "Contract Owner"}, {v: "legal", l: "Legal"}].map(r => (
                    <label key={r.v} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(inviteForm.additional_roles || []).includes(r.v)}
                        onChange={(e) => {
                          const current = inviteForm.additional_roles || [];
                          setInviteForm({
                            ...inviteForm,
                            additional_roles: e.target.checked ? [...current, r.v] : current.filter(x => x !== r.v),
                          });
                        }}
                        className="rounded"
                      />
                      {r.l}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="bg-sky-50 dark:bg-sky-950/20 border border-sky-200 dark:border-sky-800 rounded-sm p-3">
              <p className="text-xs text-sky-700 dark:text-sky-400">
                <Clock size={12} className="inline mr-1 -mt-0.5" />
                The invitation link expires in 72 hours. You can resend it if needed.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={handleSendInvite}
              disabled={inviteSending || !inviteForm.email}
              className="rounded-sm gap-2"
              data-testid="send-invite-btn"
            >
              {inviteSending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update details for {selectedUser?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-slate-800 rounded-sm">
              <div className="w-10 h-10 rounded-full bg-slate-900 dark:bg-cyan-500 flex items-center justify-center text-white dark:text-slate-900 font-medium">
                {selectedUser?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <p className="font-medium">{selectedUser?.name}</p>
                <p className="text-sm text-slate-500">{selectedUser?.email}</p>
              </div>
            </div>
            {isSuperuser && (
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-sm" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger className="rounded-sm" data-testid="role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(roleLabels).filter(([k]) => isSuperuser || k !== "superuser").map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isSuperuser && (
              <div className="space-y-2">
                <Label>Department</Label>
                <Input value={editForm.department} onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} className="rounded-sm" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleEditUser} className="rounded-sm" data-testid="update-role-btn">Update User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock size={18} /> Page Permissions</DialogTitle>
            <DialogDescription>Control which pages {permUser?.name} can access</DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-2 max-h-72 overflow-y-auto">
            {(allPages.length > 0 ? allPages : Object.keys(pageLabels)).map((page) => (
              <label key={page} className="flex items-center gap-3 p-3 border rounded-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors" data-testid={`perm-checkbox-${page}`}>
                <Checkbox checked={permList.includes(page)} onCheckedChange={() => togglePermission(page)} />
                <span className="text-sm">{pageLabels[page] || page}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSavePermissions} className="rounded-sm" data-testid="save-permissions-btn">Save Permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600"><Trash2 size={20} /> Delete User</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>You are about to permanently delete this user account:</p>
                <div className="bg-slate-50 dark:bg-slate-800 border rounded-sm p-3 my-3">
                  <p className="font-medium text-slate-900 dark:text-white">{deleteTarget?.name}</p>
                  <p className="text-xs text-slate-500 mt-1">{deleteTarget?.email} &middot; {roleLabels[deleteTarget?.role] || deleteTarget?.role}</p>
                </div>
                <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-sm p-3 text-sm text-red-700 dark:text-red-400">
                  <p className="font-semibold">This action cannot be undone.</p>
                  <p className="mt-1">All data associated with this user (including workflow actions, delegations) will be orphaned. Consider disabling the account instead.</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-delete-user-btn">Delete Permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Invite Confirmation */}
      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600"><X size={20} /> Revoke Invitation</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p>Revoke the pending invitation for:</p>
                <div className="bg-slate-50 dark:bg-slate-800 border rounded-sm p-3 my-3">
                  <p className="font-medium text-slate-900 dark:text-white">{revokeTarget?.email}</p>
                  <p className="text-xs text-slate-500 mt-1">{roleLabels[revokeTarget?.role] || revokeTarget?.role}</p>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">The invite link will no longer work. You can send a new invitation later.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevokeInvite} className="rounded-sm bg-amber-600 hover:bg-amber-700" data-testid="confirm-revoke-invite-btn">Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
